import React, { FC, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router";
import { Button, Col, FormGroup, Input, Row } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { toastOptions } from "config/constants";
import accountApi from "api/account";

const extractError = (err: any): string => {
  const data = err?.response?.data;
  if (data?.errors) {
    if (data.errors.reset_password_token) {
      return `Reset link: ${data.errors.reset_password_token}`;
    }
    if (data.errors.password) return `Password: ${data.errors.password}`;
    if (data.errors.base) return String(data.errors.base);
    if (Array.isArray(data.errors)) return data.errors.join(", ");
  }
  if (data?.error) return String(data.error);
  return err?.message || "Something went wrong";
};

const ResetPassword: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => {
    return new URLSearchParams(location.search).get("token") || "";
  }, [location.search]);

  const [password, setPassword] = useState<string>("");
  const [confirm, setConfirm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const submit = async () => {
    if (!token) {
      toast.error("Missing reset token — request a new reset email", toastOptions);
      return;
    }
    if (!password || password.length < 8) {
      toast.error("New password must be at least 8 characters", toastOptions);
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match", toastOptions);
      return;
    }
    setIsLoading(true);
    try {
      await accountApi.resetPassword(token, password);
      toast.success("Password reset — sign in with your new password", toastOptions);
      navigate("/signin");
    } catch (err) {
      toast.error(extractError(err), toastOptions);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-100 bg-plum-plate bg-animation">
      <div className="d-flex h-100 justify-content-center align-items-center">
        <Col md="8" className="mx-auto app-login-box">
          <div className="modal-dialog w-100 mx-auto">
            <div className="modal-content">
              <div className="modal-body">
                <div className="app-logo mx-auto mb-3" />
                <h4 className="text-center mb-3">Reset password</h4>
                {!token ? (
                  <p className="text-danger text-center">
                    This reset link is missing its token. Please request a new password reset email.
                  </p>
                ) : (
                  <Row form>
                    <Col md={12}>
                      <FormGroup>
                        <Input
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") submit();
                          }}
                          type="password"
                          autoComplete="new-password"
                          name="password"
                          id="password"
                          placeholder="New password..."
                        />
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Input
                          value={confirm}
                          onChange={(e) => setConfirm(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") submit();
                          }}
                          type="password"
                          autoComplete="new-password"
                          name="confirm"
                          id="confirm"
                          placeholder="Confirm new password..."
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                )}
              </div>
              <div className="modal-footer clearfix">
                <div className="float-left">
                  <Link to="/signin">Back to sign in</Link>
                </div>
                {token && (
                  <div className="float-right">
                    <Button color="primary" disabled={isLoading} size="lg" onClick={submit}>
                      Reset password
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </div>
      <ToastContainer autoClose={2000} />
    </div>
  );
};

export default ResetPassword;
